import * as LucideIcons from "lucide-react"
import type { ComponentType } from "react"
import type { Slug } from "@/sanity.types"
import type { ForestImpact } from "@/lib/settings"

export interface CO2Comparison {
  _id: string
  title: string | null
  slug: Slug | null
  icon?: string | null
  itemName: string | null
  emissionsPerItem: number | null
  calculationUnitLabel?: string | null
}

export interface CO2ComparisonDetail extends CO2Comparison {
  calculationDescription: string
  sources: Array<{
    title: string
    url: string
    description?: string
  }>
  seoTitle?: string
  seoDescription?: string
}

export interface CO2ComparisonView {
  equivalent: number
  formattedEquivalent: string
  shortDescription: string
  comparisonText: string
  calculationLine: string
  slug: string | null
  isComplete: boolean
}

export function calculateEquivalent(co2CapturedKg: number, emissionsPerItemKg: number): number {
  return Math.round(co2CapturedKg / emissionsPerItemKg)
}

export function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`
  }
  if (num >= 100_000) {
    return `${Math.round(num / 1_000)}K`
  }
  if (num >= 10_000) {
    return `${(num / 1_000).toFixed(1)}K`
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`
  }
  return num.toLocaleString()
}

function inferCalculationUnitShortLabel(itemName: string): string {
  if (itemName.includes("hours of")) return "hour"
  if (itemName.includes("emails")) return "email"
  if (itemName.includes("cups")) return "cup"
  return itemName.split(" ")[0]?.replace(/s$/, "") || "item"
}

function buildCO2ComparisonViewFromKg(
  co2CapturedKg: number,
  comparison: CO2Comparison
): CO2ComparisonView {
  const emissionsPerItem = comparison.emissionsPerItem
  const itemName = comparison.itemName
  const slug = comparison.slug?.current ?? null

  if (!emissionsPerItem || !itemName || !slug) {
    return {
      equivalent: 0,
      formattedEquivalent: "0",
      shortDescription: "Unknown comparison",
      comparisonText: "Our forest has captured enough CO₂ to offset unknown comparison",
      calculationLine: "Calculation unavailable",
      slug,
      isComplete: false,
    }
  }

  const equivalent = calculateEquivalent(co2CapturedKg, emissionsPerItem)
  const formattedEquivalent = formatLargeNumber(equivalent)
  const shortDescription = `${formattedEquivalent} ${itemName}`
  const unit = comparison.calculationUnitLabel?.trim() || inferCalculationUnitShortLabel(itemName)
  const calculationLine = `${co2CapturedKg.toLocaleString()} kg ÷ ${emissionsPerItem} kg/${unit} = ${equivalent.toLocaleString()} ${itemName}`

  return {
    equivalent,
    formattedEquivalent,
    shortDescription,
    comparisonText: `Our forest has captured enough CO₂ to offset ${shortDescription}`,
    calculationLine,
    slug,
    isComplete: true,
  }
}

export function buildCO2ComparisonView(
  forest: ForestImpact,
  comparison: CO2Comparison
): CO2ComparisonView {
  return buildCO2ComparisonViewFromKg(forest.co2CapturedKg, comparison)
}

export function resolveLucideIcon(
  iconName: string | null | undefined
): ComponentType<{ className?: string }> | null {
  if (!iconName || !(iconName in LucideIcons)) return null
  // Icon name comes from CMS; dynamic lookup is intentional.
  // biome-ignore lint/performance/noDynamicNamespaceImportAccess: CMS-driven Lucide icon name
  return LucideIcons[iconName as keyof typeof LucideIcons] as ComponentType<{
    className?: string
  }>
}

/** @deprecated Use buildCO2ComparisonView */
export function generateShortDescription(co2CapturedKg: number, comparison: CO2Comparison): string {
  return buildCO2ComparisonViewFromKg(co2CapturedKg, comparison).shortDescription
}

/** @deprecated Use buildCO2ComparisonView */
export function generateCalculationDescription(
  co2CapturedKg: number,
  comparison: CO2Comparison
): string {
  return buildCO2ComparisonViewFromKg(co2CapturedKg, comparison).calculationLine
}

/** @deprecated Use buildCO2ComparisonView */
export function generateComparisonText(co2CapturedKg: number, comparison: CO2Comparison): string {
  return buildCO2ComparisonViewFromKg(co2CapturedKg, comparison).comparisonText
}

export function formatTimeUnit(hours: number, unit: "hours" | "years"): string {
  if (unit === "years") {
    const years = Math.round(hours / (24 * 365))
    return `${years} ${years === 1 ? "year" : "years"}`
  }
  return `${formatLargeNumber(hours)} hours`
}
